'use client';

import { VoteType } from '@prisma/client';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { voteOnPhoto } from '../../../actions/reporterActions';
import { CommentData } from '../../../types/shared';
import { PhotoWithDetails } from '../page';
import { PhotoGridWithBanners } from './PhotoGridWithBanners';

// Обновленные типы событий
type ReporterEvent =
  | {
      type: 'VOTE_UPDATE';
      payload: {
        photoId: string;
        likes: number; // Только числа!
        dislikes: number;
      };
    }
  | {
      type: 'NEW_COMMENT';
      payload: CommentData & { photoId: string };
    }
  | {
      type: 'DELETE_COMMENT';
      payload: { photoId: string; commentId: string; commentIds?: string[] };
    };

function applyOptimisticVote(photo: PhotoWithDetails, voteType: VoteType): PhotoWithDetails {
  const prev = photo.myVote;
  let { likes, dislikes } = photo;

  // Toggle (убрать голос)
  if (prev === voteType) {
    if (voteType === 'LIKE') likes = Math.max(0, likes - 1);
    if (voteType === 'DISLIKE') dislikes = Math.max(0, dislikes - 1);
    return { ...photo, myVote: null, likes, dislikes };
  }

  // Switch (смена)
  if (prev === 'LIKE') likes = Math.max(0, likes - 1);
  if (prev === 'DISLIKE') dislikes = Math.max(0, dislikes - 1);

  if (voteType === 'LIKE') likes++;
  if (voteType === 'DISLIKE') dislikes++;

  return { ...photo, myVote: voteType, likes, dislikes };
}

function collectDescendantIds(comments: CommentData[], rootId: string) {
  const childrenMap = new Map<string, string[]>();
  for (const comment of comments) {
    if (!comment.parentId) continue;
    const list = childrenMap.get(comment.parentId);
    if (list) list.push(comment.id);
    else childrenMap.set(comment.parentId, [comment.id]);
  }

  const ids = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;
    const children = childrenMap.get(current) ?? [];
    for (const childId of children) {
      if (!ids.has(childId)) {
        ids.add(childId);
        queue.push(childId);
      }
    }
  }
  return ids;
}

export function PhotoGridClient({
  photos,
  initialSelectedPhoto,
  userId,
}: {
  photos: PhotoWithDetails[];
  initialSelectedPhoto: PhotoWithDetails | null;
  userId?: string;
}) {
  const [livePhotos, setLivePhotos] = useState(photos);

  useEffect(() => {
    setLivePhotos(photos);
  }, [photos]);

  useEffect(() => {
    const eventSource = new EventSource('/api/reporter/events');

    eventSource.onmessage = (event) => {
      try {
        if (event.data === ': ping') return;
        const data: ReporterEvent = JSON.parse(event.data);

        setLivePhotos((current) =>
          current.map((photo) => {
            if (photo.id !== data.payload.photoId) return photo;

            if (data.type === 'VOTE_UPDATE') {
              // Просто обновляем счетчики. myVote не трогаем (он локальный)
              // Если вдруг придут те же числа - React сам оптимизирует, но можно проверить
              if (photo.likes === data.payload.likes && photo.dislikes === data.payload.dislikes)
                return photo;

              return {
                ...photo,
                likes: data.payload.likes,
                dislikes: data.payload.dislikes,
              };
            }

            if (data.type === 'NEW_COMMENT') {
              if (photo.comments.some((c) => c.id === data.payload.id)) return photo;
              return {
                ...photo,
                comments: [...photo.comments, data.payload],
                commentCount: photo.commentCount + 1,
              };
            }

            if (data.type === 'DELETE_COMMENT') {
              const directIds = data.payload.commentIds?.length
                ? new Set(data.payload.commentIds)
                : null;

              if (directIds && !photo.comments.some((c) => directIds.has(c.id))) return photo;
              if (!directIds && !photo.comments.some((c) => c.id === data.payload.commentId))
                return photo;

              const idsToDelete = directIds ?? collectDescendantIds(photo.comments, data.payload.commentId);
              const nextComments = photo.comments.filter((c) => !idsToDelete.has(c.id));
              const removedCount = photo.comments.length - nextComments.length;
              return {
                ...photo,
                comments: nextComments,
                commentCount: Math.max(0, photo.commentCount - removedCount),
              };
            }
            return photo;
          }),
        );
      } catch (error) {
        console.error('SSE Error:', error);
      }
    };

    return () => eventSource.close();
  }, []);

  const handleVote = useCallback(
    async (photoId: string, voteType: VoteType) => {
      if (!userId) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      // 1. Оптимистичное обновление
      setLivePhotos((current) =>
        current.map((p) => (p.id === photoId ? applyOptimisticVote(p, voteType) : p)),
      );

      // 2. Запрос на сервер
      try {
        await voteOnPhoto(photoId, voteType);
      } catch (err) {
        toast.error('Не удалось сохранить голос');
        // Здесь можно сделать откат (rollback), если нужно
      }
    },
    [userId],
  );

  const handleCommentAdded = useCallback((photoId: string, comment: CommentData) => {
    setLivePhotos((current) =>
      current.map((photo) => {
        if (photo.id !== photoId) return photo;
        if (photo.comments.some((c) => c.id === comment.id)) return photo;
        return {
          ...photo,
          comments: [...photo.comments, comment],
          commentCount: photo.commentCount + 1,
        };
      }),
    );
  }, []);

  return (
    <PhotoGridWithBanners
      photos={livePhotos}
      initialSelectedPhoto={initialSelectedPhoto}
      onVote={handleVote}
      onCommentAdded={handleCommentAdded}
    />
  );
}
